import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MainContactCard } from "./dealership/MainContactCard";
import { BranchInfoCard } from "./dealership/BranchInfoCard";
import { MainDataChangeCard } from "./dealership/MainDataChangeCard";
import { ServicesCard } from "./dealership/ServicesCard";
import { SocialMediaCard } from "./dealership/SocialMediaCard";
import { DealerAttributesCard } from "./dealership/DealerAttributesCard";
import { SiteStatusCard } from "./dealership/SiteStatusCard";
import { EmployeesCard } from "./dealership/EmployeesCard";
import { WebModulesCard } from "./dealership/WebModulesCard";
import { CustomDataCard } from "./dealership/CustomDataCard";
import { InternalContactCard } from "./dealership/InternalContactCard";
import { DealerLogoCard } from "./dealership/DealerLogoCard";
import { CookieManagerCard } from "./dealership/CookieManagerCard";
import { CookieConsentsCard } from "./dealership/CookieConsentsCard";
import { SadConfirmationsCard } from "./dealership/SadConfirmationsCard";
import { MdHashField } from "./dealership/MdHashField";
import { FeedPathTag } from "@/components/FeedPathTag";
import { DealerVersionTag } from "@/components/VersionTag";
import { DealerTagBadges } from "@/components/DealerTagBadges";
import { PERM, hasPermission, isMhqUser } from "@/lib/permissions";
import { ArrowLeft, ScrollText } from "lucide-react";

export default function MyDealership({ mode = "self" }) {
  const routeParams = useParams();
  const navigate = useNavigate();
  const { user, impersonating } = useAuth();
  const { t } = useI18n();

  const isMhq = isMhqUser(user);
  const dealerId =
    mode === "byId" ? routeParams.id : mode === "mine" ? routeParams.dealerId : impersonating?.id || user?.dealer_id;

  // Every gate below is scoped to THIS dealer. A multi-dealer user can hold
  // different roles on different dealers, so a permission must never be
  // derived from the flat/display-only `user.roles` union.
  const can = (permission) => hasPermission(user, dealerId, permission);
  const canEditHost = can(PERM.HOST_NAME_EDIT);
  const canEditMainContact = can(PERM.MAIN_CONTACT_EDIT);
  const canEditBranch = can(PERM.BRANCH_EDIT);
  const canEditServices = can(PERM.SERVICES_EDIT);
  const canManageServices = can(PERM.SERVICES_MANAGE);
  const canManageCertifications = can(PERM.CERTIFICATIONS_EDIT);
  const canEditSocialMedia = can(PERM.SOCIAL_MEDIA_EDIT);
  const canManageEmployees = can(PERM.EMPLOYEES_MANAGE);
  const canManageContent = can(PERM.CONTENT_MANAGE);
  const canManageCustomData = can(PERM.CUSTOM_DATA_EDIT);
  const canEditInternalContact = can(PERM.INTERNAL_CONTACT_EDIT);
  const canEditLogo = can(PERM.LOGO_EDIT);
  const canManageCookieConsents = can(PERM.COOKIE_CONSENTS_MANAGE);
  const canEditSadConfirmations = can(PERM.SAD_CONFIRMATIONS_EDIT);
  const canViewMdHash = can(PERM.MD_HASH_VIEW);
  const canManageDdmMigration = can(PERM.DDM_MIGRATION_MANAGE);
  const canToggleSiteStatus = can(PERM.SITE_STATUS_TOGGLE);
  const canDeploySite = can(PERM.SITE_DEPLOY);
  const canAssignExistingSite = can(PERM.SITE_ASSIGN_EXISTING);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importToggling, setImportToggling] = useState(false);
  const [syncedFields, setSyncedFields] = useState([]);

  // Coordinates stay off-limits while impersonating: an MHQ user acting as
  // a dealer shouldn't silently move a pin on that dealer's behalf.
  const canEditCoords = can(PERM.BRANCH_COORDINATES_EDIT) && !impersonating;

  useEffect(() => {
    if (!dealerId) return;
    let ignore = false;
    setLoading(true);
    client.get(`/dealers/${dealerId}/summary`).then(({ data }) => {
      if (!ignore) setSummary(data);
    }).finally(() => {
      if (!ignore) setLoading(false);
    });
    return () => { ignore = true; };
  }, [dealerId]);

  useEffect(() => {
    let ignore = false;
    client.get("/dealers/ddm-synced-fields").then(({ data }) => {
      if (!ignore) setSyncedFields(data.fields || []);
    }).catch(() => {});
    return () => { ignore = true; };
  }, []);

  if (loading || !summary) {
    return (
      <div className="mx-auto max-w-6xl p-6 md:p-10">
        <Skeleton className="mb-6 h-10 w-64" />
        <Skeleton className="mb-4 h-40" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const dealer = summary.dealer;
  const branch = summary.branches?.[0];
  const services = summary.services || [];
  const employees = summary.employees || [];
  const webModules = summary.web_modules || [];

  const setDealerFromCard = (freshDealer) =>
    setSummary((s) => ({ ...s, dealer: freshDealer }));

  // Employees/web-modules endpoints return only the mutated record, not the
  // dealer — so dealer.card_versions/version (shown via VersionTag) would
  // otherwise stay stale in this page's state until a full reload. Cheap
  // re-fetch after any employee/web-module mutation keeps it live.
  const refreshDealerVersion = async () => {
    try {
      const { data } = await client.get(`/dealers/${dealerId}/summary`);
      setDealerFromCard(data.dealer);
    } catch {
      // Best-effort — the mutation itself already succeeded and is
      // reflected in the employees/web-modules list regardless.
    }
  };

  const setBranchFromCard = (freshBranch) =>
    setSummary((s) => ({
      ...s,
      branches: s.branches.map((b) => (b.id === freshBranch.id ? freshBranch : b)),
    }));

  const setServiceFromCard = (freshService) =>
    setSummary((s) => ({
      ...s,
      services: s.services.map((x) => (x.id === freshService.id ? freshService : x)),
    }));

  const setServicesFromCard = (updater) =>
    setSummary((s) => ({ ...s, services: updater(s.services || []) }));

  const setEmployeesFromCard = (updater) =>
    setSummary((s) => ({ ...s, employees: updater(s.employees || []) }));

  const setWebModulesFromCard = (updater) =>
    setSummary((s) => ({ ...s, web_modules: updater(s.web_modules || []) }));

  const toggleDdmMigrated = async () => {
    const nextVal = !dealer.management_migrated_to_ddm;
    setImportToggling(true);
    try {
      const { data } = await client.patch(`/dealers/${dealerId}/ddm-migration`, { migrated: nextVal });
      setDealerFromCard(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update DDM migration flag");
    } finally {
      setImportToggling(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-10" data-testid="my-dealership-page">
      {mode === "byId" && (
        <button
          data-testid="back-to-list"
          onClick={() => navigate("/dealers")}
          className="mb-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to Dealers
        </button>
      )}
      {mode === "mine" && (
        <button
          data-testid="back-to-my-dealerships"
          onClick={() => navigate("/my-dealerships")}
          className="mb-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("nav.myDealerships")}
        </button>
      )}

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <p className="data-label">{mode === "self" ? "MY DEALERSHIP" : "DEALER DETAILS"}</p>
            {dealer.deleted && (
              <Badge variant="destructive" data-testid="dealer-deleted-badge">
                {t("dealers.deletedBadge")}
              </Badge>
            )}
            {canManageDdmMigration && (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="dealer-ddm-migrated-label">
                <Checkbox
                  data-testid="dealer-ddm-migrated"
                  checked={dealer.management_migrated_to_ddm === true}
                  disabled={importToggling}
                  onCheckedChange={toggleDdmMigrated}
                />
                {t("dealership.managementMigratedToDdm")}
              </label>
            )}
          </div>
          <h1 className="mt-2 flex items-center gap-1.5 text-3xl font-bold tracking-tight md:text-4xl">
            {dealer.name}
            <FeedPathTag path="Dealer.Name" />
          </h1>
          <DealerTagBadges tags={dealer.tags} className="mt-2" />
          <p className="mt-1 flex flex-wrap items-center gap-1 font-mono text-xs text-muted-foreground">
            {dealer.global_id}
            <FeedPathTag path="Dealer.GlobalID" />
            · Company {dealer.company_id}
            <FeedPathTag path="Dealer.CompanyNO" />
          </p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              {t("dealership.kvpsId")}: {dealer.kvps_id || "—"}
              <FeedPathTag path="Dealer.KvpsID" />
            </span>
            <span className="inline-flex items-center gap-1">
              {t("dealership.kvpsNo")}: {dealer.kvps_no || "—"}
              <FeedPathTag path="Dealer.KvpsNO" />
            </span>
            <span className="inline-flex items-center gap-1">
              {t("dealership.kvpsNoId")}: {dealer.kvps_no_id || "—"}
              <FeedPathTag path="Dealer.KvpsNOID" />
            </span>
            <span className="inline-flex items-center gap-1">
              {t("dealership.companyInternalId")}: {dealer.company_internal_id || "—"}
              <FeedPathTag path="Dealer.CompanyID" />
            </span>
            <DealerVersionTag dealer={dealer} />
          </div>
          {canViewMdHash && <MdHashField dealerId={dealer.id} />}
        </div>
        <Button
          data-testid="view-legal-btn"
          variant="outline"
          onClick={() =>
            navigate(
              mode === "byId"
                ? `/dealers/${dealer.id}/legal-pages`
                : mode === "mine"
                ? `/my-dealerships/${dealer.id}/legal-pages`
                : "/legal-pages",
            )
          }
          className="gap-1.5"
        >
          <ScrollText className="h-4 w-4" />
          {t("dealership.linkLegal")}
        </Button>
      </div>

      {canEditInternalContact && <InternalContactCard dealer={dealer} dealerId={dealerId} onSaved={setDealerFromCard} />}
      <SiteStatusCard dealer={dealer} dealerId={dealerId} onSaved={setDealerFromCard} canToggleStatus={canToggleSiteStatus} canDeploy={canDeploySite} canEditHost={canEditHost} canAssignExistingSite={canAssignExistingSite} />
      <MainContactCard dealer={dealer} dealerId={dealerId} onSaved={setDealerFromCard} syncedFields={syncedFields} canManage={canEditMainContact} isMhq={isMhq} />
      <BranchInfoCard dealer={dealer} branch={branch} canManage={canEditBranch} canEditCoords={canEditCoords} onBranchSaved={setBranchFromCard} syncedFields={syncedFields} />
      <MainDataChangeCard dealer={dealer} branch={branch} />
      {canEditLogo && <DealerLogoCard dealer={dealer} dealerId={dealerId} canManage={canEditLogo} />}
      <ServicesCard
        services={services}
        onServiceSaved={setServiceFromCard}
        onServicesChanged={setServicesFromCard}
        branch={branch}
        onBranchSaved={setBranchFromCard}
        dealerId={dealerId}
        canManage={canEditServices}
        canAddRemove={canManageServices}
      />
      {canEditSocialMedia && <SocialMediaCard dealer={dealer} dealerId={dealerId} onSaved={setDealerFromCard} />}
      <DealerAttributesCard dealer={dealer} dealerId={dealerId} onSaved={setDealerFromCard} syncedFields={syncedFields} canManage={canManageCertifications} />
      {canManageEmployees && <EmployeesCard cardVersion={dealer.card_versions?.employees} employees={employees} dealerId={dealerId} onEmployeesChanged={setEmployeesFromCard} onDealerVersionChanged={refreshDealerVersion} isMhq={isMhq} />}
      {canManageContent && <WebModulesCard cardVersion={dealer.card_versions?.web_modules} webModules={webModules} dealerId={dealerId} onWebModulesChanged={setWebModulesFromCard} onDealerVersionChanged={refreshDealerVersion} />}
      {canManageCustomData && <CustomDataCard dealer={dealer} dealerId={dealerId} onSaved={setDealerFromCard} canManage={canManageCustomData} isMhq={isMhq} />}
      {canManageCookieConsents && <CookieManagerCard dealer={dealer} />}
      {canManageCookieConsents && <CookieConsentsCard dealer={dealer} dealerId={dealerId} onSaved={setDealerFromCard} canManage={canManageCookieConsents} />}
      {canEditSadConfirmations && <SadConfirmationsCard dealer={dealer} dealerId={dealerId} onSaved={setDealerFromCard} />}
    </div>
  );
}
